import { Schema } from "prosemirror-model";
import { toggleMark } from "prosemirror-commands";

import { BaseExtension } from "@/wc/text-editor/lib/core/base-extension";
import type {
    MarkdownSerializerConfig,
    ParseSpecMap,
} from "@/wc/text-editor/lib/core/types";
import type { MenuFunction } from "@/wc/text-editor/lib/core/menu";
import { markActive } from "@/wc/text-editor/lib/core/menu";

import MarkdownIt from "markdown-it";

export class StrikethroughMarkExtension extends BaseExtension {
    // Enable strikethrough mark in MarkdownIt (disabled in standard CommonMark)
    modifyMarkdownIt = (base: MarkdownIt): MarkdownIt => {
        return base.enable("strikethrough");
    };

    // Add strikethrough mark to the schema
    modifySchema = (currentSchema: Schema) => {
        return new Schema({
            nodes: currentSchema.spec.nodes,
            marks: currentSchema.spec.marks.addToEnd("strikethrough", {
                parseDOM: [
                    { tag: "s" },
                    { style: "text-decoration=line-through" },
                ],
                toDOM() {
                    return ["s"];
                },
                inclusive: false,
            }),
        });
    };

    modifyParser = (currentParser: ParseSpecMap): ParseSpecMap => {
        return {
            ...currentParser,
            s: { mark: "strikethrough" },
        };
    };

    modifySerializer = (currentConfig: MarkdownSerializerConfig) => {
        return {
            ...currentConfig,
            marks: {
                ...currentConfig.marks,
                strikethrough: {
                    open: "~~",
                    close: "~~",
                    mixable: true,
                    expelEnclosingWhitespace: true,
                },
            },
        };
    };

    menu?: (currentMenu: MenuFunction) => MenuFunction = (
        currentMenuFunction
    ) => {
        return (view) => {
            const toggleStrikethrough = toggleMark(
                view.state.schema.marks.strikethrough
            );
            return [
                ...currentMenuFunction(view),
                {
                    type: "toggle",
                    run: (state, dispatch, view) => {
                        toggleStrikethrough(state, dispatch);
                        view.focus();
                    },
                    icon: { name: "type-strikethrough" },
                    label: "Strikethrough",
                    name: "strikethrough",
                    active: (state) =>
                        markActive(state, state.schema.marks.strikethrough),
                    refocus: true,
                    order: 26,
                    group: "base",
                },
            ];
        };
    };
}
